import type { TextFieldProps } from './TextField';
import type { TextInputProps } from '@natter-co/design-system';

/**
 * Maps the legacy MUI `TextField` props onto the Design System 2.0 `TextInput` API.
 *
 * TODO(HNS-264): temporary. This mapper exists only so the visual refresh can be gated inside the
 * wrapper instead of at the 73-odd files that reach this component (most of them through
 * `ReactHookForm/TextField`); retiring the flag means deleting it and letting call sites speak the
 * DS vocabulary directly.
 *
 * Returning `null` means "the DS input cannot express this field" and sends the caller back to MUI.
 * A silent best-effort mapping is worse than no mapping here: an input is where a wrong size or a
 * dropped slot prop is least visible at review time and most disruptive in use.
 */
type DsProps = Pick<TextInputProps, 'label' | 'helperText' | 'error' | 'leftIcon'> &
  Pick<TextInputProps, 'value' | 'defaultValue'> & {
    /**
     * Set only for a multiline field, so the wrapper knows to render `TextArea` rather than
     * `TextInput`. The two shapes take the same mapped props otherwise, which is why this is a
     * flag on one result rather than two separate return types.
     */
    multiline?: true;
    /** Visible lines at rest. Only ever set alongside `multiline`. */
    rows?: number;
    /** Growth cap in lines. Only ever set alongside `multiline`. */
    maxRows?: number;
    /** The native input attributes `inputProps` is allowed to carry — see `readInputAttributes`. */
    min?: number | string;
    step?: number | string;
    maxLength?: number;
    inputMode?: InputMode;
  };

type InputMode = NonNullable<TextInputProps['inputMode']>;

const INPUT_MODES: readonly string[] = [
  'none',
  'text',
  'tel',
  'url',
  'email',
  'numeric',
  'decimal',
  'search',
] satisfies InputMode[];

type InputAttributes = Pick<DsProps, 'min' | 'step' | 'maxLength' | 'inputMode'>;

/**
 * Reads the four native attributes the DS control takes straight off `inputProps`, or `null` when
 * the bag carries anything else.
 *
 * `inputProps` addresses MUI's hidden `<input>`, so most of what a caller can put there (`aria-*`,
 * `readOnly`, `data-*`, a `ref`) has no home on the DS field and would vanish. These four are the
 * exception: they are plain `<input>` attributes, and the DS control is a Base UI `Field.Control`
 * that renders a real `<input>` and forwards them. A key with an `undefined` value is treated as
 * absent, because that is what MUI does with it.
 */
const readInputAttributes = (inputProps: NonNullable<TextFieldProps['inputProps']>): InputAttributes | null => {
  const { min, step, maxLength, inputMode, ...others } = inputProps;

  if (Object.values(others).some((value) => value !== undefined)) {
    return null;
  }

  if (
    (min !== undefined && typeof min !== 'number' && typeof min !== 'string') ||
    (step !== undefined && typeof step !== 'number' && typeof step !== 'string') ||
    (maxLength !== undefined && typeof maxLength !== 'number') ||
    (inputMode !== undefined && !INPUT_MODES.includes(inputMode))
  ) {
    return null;
  }

  return { min, step, maxLength, inputMode };
};

/**
 * MUI types `value` and `defaultValue` as `unknown`, because its input accepts whatever the caller
 * hands it and stringifies at the DOM boundary. The DS control is typed to what an `<input>` can
 * actually take, so the value has to be narrowed rather than cast — a cast here would let an object
 * through to `value=` and render `[object Object]`.
 */
const isControlValue = (value: unknown): value is string | number | readonly string[] =>
  typeof value === 'string' || typeof value === 'number' || Array.isArray(value);

/**
 * Cases the DS input cannot express, all of which stay on MUI:
 *
 *   - anything not `fullWidth` — the DS root is `w-full` unconditionally
 *     (`textInputRootRecipe`), whereas an MUI field without `fullWidth` takes its own intrinsic
 *     width. Mapping those would stretch the field and reflow its neighbours, which is the same
 *     silent-resize trap `Buttons/Button/toDsProps` avoids by matching on height rather than name.
 *   - `maxRows` or `rows` on a single-line field — MUI ignores both there, so honouring either would
 *     change the shape rather than preserve it. On a `multiline` field `maxRows` maps (DS
 *     `TextArea` grows to that many lines and then scrolls), but only as a number: MUI also
 *     accepts a string, and coercing it is the quiet transformation this mapper refuses to make.
 *   - `prefix` on a multiline field — DS `TextArea` draws no icon slots. (`suffix` refuses for
 *     every shape now; see its own entry below.)
 *   - `sx` — an Emotion prop with no effect on a Tailwind component. Accepting it would silently
 *     drop the styling rather than fail.
 *   - `InputProps`, and any `inputProps` beyond `min`, `step`, `maxLength` and `inputMode` — these
 *     address MUI's own slots (adornments, `readOnly`, `aria-*`). The DS input has no such
 *     internals, so they would vanish. The four exceptions are plain `<input>` attributes the DS
 *     control forwards untouched; see `readInputAttributes`. `min` and `step` also refuse on a
 *     multiline field, because a `<textarea>` has neither. `maxLength` on a multiline field maps
 *     too, and brings the DS character counter with it — the one visible addition.
 *   - `size` — the refreshed input has a single height and no size axis.
 *   - a non-string or absent `label` — DS requires `label: string`, because the floating label is
 *     the field's only visible name. The legacy prop is an optional `ReactNode`.
 *   - `shrinkLabel` — the DS label shrinks off the presence of a value on its own and exposes no
 *     override, so honouring this would mean pinning a label the component intends to animate.
 *   - `wrapHint={false}` — a `noWrap` on the MUI hint (`FormFieldHint`). The DS helper text has no
 *     equivalent, and forcing it would clip the message instead of wrapping it.
 *   - `variant` other than `outlined` — the wrapper hardcodes `outlined` on the MUI element, so a
 *     caller passing `filled` or `standard` today already gets something the DS cannot draw.
 *   - **`suffix`** — the newest entry, and the one that reads as a regression until you know why.
 *     It used to map onto `rightIcon`, a plain `ReactNode` slot. The refreshed field draws its
 *     trailing affordance as a `Button` (`Size=ExtraSmall, Type=Muted, iconOnly=Yes`), so the DS
 *     side is now `rightButton` + `rightButtonLabel` + `onRightButtonClick` — a named, handled
 *     control. A legacy `suffix` is an unnamed, unhandled node, so there is nothing to derive the
 *     other two from: mapping it would render a button that a screen reader announces as "button"
 *     and that does nothing when pressed. That is precisely the silent best-effort this file
 *     exists to refuse.
 *
 * `prefix` still maps, onto `leftIcon`: both are decorative `ReactNode` glyphs, and the legacy
 * wrapper only ever wrapped it in an `InputAdornment` for positioning.
 *
 * Costs nothing today, which is worth recording so the trade-off can be re-judged rather than
 * re-derived: exactly one call site in the app passes `suffix` (`question/.../ChoiceItem`), and it
 * also passes `InputProps`, so it was already returning `null` here and already staying on MUI. A
 * future field that wants a real trailing button should speak the DS vocabulary directly — which is
 * what retiring this mapper means anyway (HNS-264).
 */
export const toDsProps = ({
  label,
  hint,
  error,
  fullWidth,
  multiline,
  rows,
  maxRows,
  size,
  sx,
  variant,
  InputProps,
  inputProps,
  prefix,
  suffix,
  shrinkLabel,
  wrapHint = true,
  value,
  defaultValue,
}: TextFieldProps): DsProps | null => {
  if (!fullWidth) {
    return null;
  }

  if (
    (value !== undefined && !isControlValue(value)) ||
    (defaultValue !== undefined && !isControlValue(defaultValue))
  ) {
    return null;
  }

  /**
   * The refreshed set draws a textarea, so `multiline` maps, `maxRows` included: `TextArea` grows
   * to that many lines and then scrolls, which is what MUI does.
   *
   * What still refuses is narrower and deliberate. `rows` and `maxRows` on a single-line field ask
   * for lines MUI ignores today, so honouring them would change the shape.
   */
  if (!multiline && (rows !== undefined || maxRows !== undefined)) {
    return null;
  }

  if (maxRows !== undefined && typeof maxRows !== 'number') {
    return null;
  }

  /**
   * `TextArea` draws no icon slots. `suffix` is refused for every shape a few lines below, so
   * `prefix` is all this has left to catch.
   */
  if (multiline && prefix !== undefined) {
    return null;
  }

  /**
   * MUI accepts `rows="7"` as well as `rows={7}`; DS `TextArea` types it as a number, because it
   * rides the `<textarea>` rather than the field root. Coercing the string would work and is
   * exactly the kind of quiet transformation this mapper refuses to make - a caller who wrote a
   * string keeps today's field.
   */
  if (rows !== undefined && typeof rows !== 'number') {
    return null;
  }

  if (sx || InputProps || size || shrinkLabel || !wrapHint) {
    return null;
  }

  const inputAttributes = inputProps ? readInputAttributes(inputProps) : {};

  if (!inputAttributes) {
    return null;
  }

  // A `<textarea>` has no numeric range, so these would be dropped rather than honoured.
  if (multiline && (inputAttributes.min !== undefined || inputAttributes.step !== undefined)) {
    return null;
  }

  // See the `suffix` entry above: there is no longer a DS slot a bare node can fill.
  if (suffix) {
    return null;
  }

  if (variant !== undefined && variant !== 'outlined') {
    return null;
  }

  if (typeof label !== 'string') {
    return null;
  }

  if (multiline) {
    const { maxLength, inputMode } = inputAttributes;

    return {
      label,
      helperText: hint,
      error,
      value,
      defaultValue,
      multiline: true,
      rows,
      maxRows,
      maxLength,
      inputMode,
    };
  }

  return { label, helperText: hint, error, leftIcon: prefix, value, defaultValue, ...inputAttributes };
};
